import { useQuery, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  GET_BUSINESS_HOURS_CONFIG,
  GET_HOURLY_SALES,
  GET_HOURLY_STATUS,
  GET_DAILY_SUMMARY,
  UPDATE_BUSINESS_HOURS_CONFIG,
  UPDATE_HOURLY_SALES,
} from '../graphql/businessHoursOperations';

// ==================== TYPES ====================

export interface BusinessHoursConfig {
  id: string;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  totalHours: number;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface HourlySalesTracking {
  id: string;
  date: Date;
  hour: number;
  salesAmount: number;
  salesCount: number;
  targetAmount: number;
  targetPerHour: number;
  isBehind: boolean;
  isOnTrack: boolean;
  isAhead: boolean;
  percentage: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface HourlyStatus {
  hour: number;
  // ===== MODO ESPECIAL POS (Monto Diario) =====
  salesAmount: number;
  salesCount: number;
  targetPerHour: number;
  adaptiveTargetPerHour: number;
  percentage: number;
  adaptivePercentage: number;
  isBehind: boolean;
  isOnTrack: boolean;
  isAhead: boolean;
  isPastHour: boolean;
  isCurrentHour: boolean;
  // ===== CONTROL POR IMPUESTO (Facturación Electrónica) =====
  electronicSalesAmount: number;
  electronicSalesCount: number;
  electronicTaxAmount: number;
  electronicTargetPerHour: number;
  electronicAdaptiveTarget: number;
  electronicPercentage: number;
  electronicAdaptivePercentage: number;
  electronicIsBehind: boolean;
  electronicIsOnTrack: boolean;
  electronicIsAhead: boolean;
}

export interface DailySummary {
  totalSales: number;
  totalCount: number;
  targetPerHour: number;
  hoursCompleted: number;
  hoursBehind: number;
  hoursOnTrack: number;
  hoursAhead: number;
  overallPercentage: number;
}

export interface UpdateBusinessHoursInput {
  startHour?: number;
  startMinute?: number;
  endHour?: number;
  endMinute?: number;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
}

// ==================== HOOKS ====================

/**
 * Hook para obtener la configuración de horarios laborales
 */
export const useBusinessHoursConfig = () => {
  const { data, loading, error, refetch } = useQuery<{
    getBusinessHoursConfig: BusinessHoursConfig;
  }>(GET_BUSINESS_HOURS_CONFIG, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    config: data?.getBusinessHoursConfig,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para obtener ventas por hora de un día específico
 * @param pollInterval - Intervalo de polling en ms (default: 10000 = 10 segundos)
 */
export const useHourlySales = (date: Date, pollInterval: number = 10000) => {
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<{
    getHourlySales: HourlySalesTracking[];
  }>(GET_HOURLY_SALES, {
    variables: { date },
    fetchPolicy: 'network-only',
    pollInterval: pollInterval,
  });

  return {
    sales: data?.getHourlySales || [],
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
};

/**
 * Hook para obtener el estado de cada hora del día
 * @param pollInterval - Intervalo de polling en ms (default: 10000 = 10 segundos)
 */
export const useHourlyStatus = (date: Date, dailyTarget: number, pollInterval: number = 10000) => {
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<{
    getHourlyStatus: HourlyStatus[];
  }>(GET_HOURLY_STATUS, {
    variables: { date, dailyTarget },
    fetchPolicy: 'network-only',
    skip: !dailyTarget || dailyTarget === 0,
    pollInterval: pollInterval,
  });

  return {
    status: data?.getHourlyStatus || [],
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
};

/**
 * Hook para obtener el resumen del día
 * @param pollInterval - Intervalo de polling en ms (default: 10000 = 10 segundos)
 */
export const useDailySummary = (date: Date, dailyTarget: number, pollInterval: number = 10000) => {
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<{
    getDailySummary: DailySummary;
  }>(GET_DAILY_SUMMARY, {
    variables: { date, dailyTarget },
    fetchPolicy: 'network-only',
    skip: !dailyTarget || dailyTarget === 0,
    pollInterval: pollInterval,
  });

  return {
    summary: data?.getDailySummary,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
};

/**
 * Hook para actualizar la configuración de horarios
 */
export const useUpdateBusinessHours = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_BUSINESS_HOURS_CONFIG, {
    refetchQueries: [
      { query: GET_BUSINESS_HOURS_CONFIG },
    ],
  });

  const updateConfig = useCallback(
    async (input: UpdateBusinessHoursInput) => {
      const result = await updateMutation({ variables: { input } });
      return result.data?.updateBusinessHoursConfig;
    },
    [updateMutation]
  );

  return {
    updateConfig,
    updating: loading,
  };
};

/**
 * Hook para actualizar ventas de una hora específica
 */
export const useUpdateHourlySales = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_HOURLY_SALES, {
    refetchQueries: [
      { query: GET_HOURLY_SALES },
      { query: GET_HOURLY_STATUS },
      { query: GET_DAILY_SUMMARY },
    ],
  });

  const updateSales = useCallback(
    async (
      date: Date,
      hour: number,
      salesAmount: number,
      salesCount: number,
      dailyTarget: number
    ) => {
      const result = await updateMutation({
        variables: { date, hour, salesAmount, salesCount, dailyTarget },
      });
      return result.data?.updateHourlySales;
    },
    [updateMutation]
  );

  return {
    updateSales,
    updating: loading,
  };
};
